import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Switch,
  Text,
} from "@chakra-ui/react";
import { ArrowRight, ChartNoAxesCombined } from "lucide-react";
import { useEffect, useState } from "react";
import { track } from "../../../helpers/posthog";
import { palette } from "./components/ui";
import { AnalyticsDashboard, AnalyticsToolbar } from "./AnalyticsContainer";
import { currentYearMonth } from "@/helpers/period";
import CommonLayout from "../CommonLayout";
import Footer from "../../../components/Footer";
import SelectFilter from "../../../components/SelectFilter";
import { useMerchant } from "../../../hooks/useMerchant";
import { useFetchUser } from "../../../hooks/useFetchUser";
import { useTRPC } from "../../../trpc/client";
import StatsGrid from "./components/StatsGrid";
import OnboardingChecklist from "./components/OnboardingChecklist";
import { useQuery } from "@tanstack/react-query";

const months = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const START_YEAR = 2024;
const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) =>
  (START_YEAR + i).toString()
);

const AnalyticsBanner = ({
  onEnableNew,
}: {
  onEnableNew: () => void;
}): JSX.Element => {
  return (
    <Flex
      align="center"
      gap={3}
      px={4}
      py={4.5}
      borderRadius="12px"
      color="white"
      background={`linear-gradient(135deg, ${palette.purple} 0%, ${palette.purpleDark} 100%)`}
      cursor="pointer"
      onClick={() => {
        track("analytics_banner_clicked");
        onEnableNew();
      }}
    >
      <Flex
        w="32px"
        h="32px"
        borderRadius="full"
        bg="whiteAlpha.200"
        align="center"
        justify="center"
        flex="none"
      >
        <ChartNoAxesCombined size={16} />
      </Flex>
      <Box flex="1">
        <Text fontSize="14px" fontWeight="700" lineHeight="short">
          Nueva analítica de recaudo
        </Text>
        <Text fontSize="12px" opacity={0.85} lineHeight="short">
          Este resumen se reemplazará próximamente. El centro de mando prioriza
          la cobranza, segmenta los inquilinos y proyecta el cierre del mes.
        </Text>
      </Box>
      <Button
        size="sm"
        bg="white"
        color={palette.purple}
        borderRadius="full"
        fontWeight="700"
        flex="none"
        _hover={{ bg: palette.soft }}
      >
        Activar la nueva analítica
        <ArrowRight size={14} />
      </Button>
    </Flex>
  );
};

const useLegacySummary = (onEnableNew: () => void) => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const { user } = useFetchUser();
  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth();
  const currentYearVal = currentDate.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonthIndex);
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYearVal.toString()
  );
  const [useRealtimeAnalytics, setUseRealtimeAnalytics] =
    useState<boolean>(true);

  const formattedYearMonth = `${selectedYear}-${(selectedMonth + 1)
    .toString()
    .padStart(2, "0")}`;

  const metrics = useQuery({
    ...trpc.invoices.retrieveMetrics.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
      yearMonth: formattedYearMonth,
      useRealtimeAnalytics,
    }),
    enabled: !!merchant?.id,
  });

  const handleSelect = (option: string | undefined) => {
    if (option) {
      const newMonthIndex = months.indexOf(option);
      setSelectedMonth(newMonthIndex);
    }
  };

  return {
    subtitle: useRealtimeAnalytics
      ? "Analitica de tus recaudos en tiempo real"
      : "Estos datos se actualizan todos los días a las 7 am",
    buttons: (
      <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        {user?.admin && (
          <Switch.Root
            checked={useRealtimeAnalytics}
            onCheckedChange={({ checked }) => setUseRealtimeAnalytics(checked)}
            colorScheme="purple"
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label>
              <Text fontSize="sm" whiteSpace="nowrap">
                Analítica en tiempo real
              </Text>
            </Switch.Label>
          </Switch.Root>
        )}
        <SelectFilter
          options={years}
          selectedOption={selectedYear}
          onSelect={(option) => setSelectedYear(option || "")}
          placeholder={currentYearVal.toString()}
          width="12.5rem"
          isClearable={false}
        />
        <SelectFilter
          options={months}
          selectedOption={months[selectedMonth]}
          onSelect={handleSelect}
          placeholder={months[currentMonthIndex]}
          width="12.5rem"
          isClearable={false}
        />
      </div>
    ),
    content: (
      <>
        <AnalyticsBanner onEnableNew={onEnableNew} />
        {metrics.isLoading ? (
          <Box flexGrow={1} height="100%">
            <Center height="100%">
              <Spinner size="xl" />
            </Center>
          </Box>
        ) : metrics.data ? (
          <StatsGrid
            metrics={metrics.data}
            detailMetrics={metrics.data.metrics}
          />
        ) : (
          <></>
        )}
      </>
    ),
  };
};

const STORAGE_KEY = "new-analytics";

const SummaryContainer = (): JSX.Element => {
  const { merchant } = useMerchant();
  const [newAnalytics, setNewAnalytics] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true"
  );
  const [cutOffPeriod, setCutOffPeriod] = useState<string | undefined>(
    currentYearMonth
  );
  const legacy = useLegacySummary(() => setNewAnalytics(true));

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(newAnalytics));
  }, [newAnalytics]);

  useEffect(() => {
    if (newAnalytics && merchant?.id)
      track("analytics_viewed", { merchant_id: merchant.id });
  }, [newAnalytics, merchant?.id]);

  return (
    <CommonLayout
      title={newAnalytics ? "Centro de mando de recaudo" : "Resumen"}
      subtitle={
        newAnalytics
          ? "Analitica operativa para priorizar cobranza, reducir friccion y anticipar liquidez."
          : legacy.subtitle
      }
      buttons={
        newAnalytics ? (
          <AnalyticsToolbar
            cutOffPeriod={cutOffPeriod}
            onPeriodChange={(period) => {
              setCutOffPeriod(period);
              if (period) track("analytics_period_changed", { period });
            }}
            onRestoreLegacy={() => setNewAnalytics(false)}
          />
        ) : (
          legacy.buttons
        )
      }
      table={
        newAnalytics ? (
          <AnalyticsDashboard cutOffPeriod={cutOffPeriod} />
        ) : (
          legacy.content
        )
      }
      footer={<Footer />}
      sticky
      scrollResetKey={newAnalytics}
    >
      <OnboardingChecklist />
    </CommonLayout>
  );
};

export default SummaryContainer;
